import { QRCode } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { ILifeInsurance } from "@base/modules/patientInsurance/lifeInsurance.interface";
import { ICardTemplate } from "./cardTemplates";

/*
 * Android lays the overlay out in dp on a card about 345dp wide (`row_card_front.xml`,
 * `row_card_back.xml`). Those offsets are carried over here in `cqw` - percent of the card's own
 * width - so the text and QR stay on the artwork at whatever width the card is drawn.
 */

interface IFaceProps {
  image: string;
  children: React.ReactNode;
}

function CardFace({ image, children }: IFaceProps) {
  return (
    <div className="relative w-full overflow-hidden rounded-xl text-white [container-type:inline-size]">
      {/* The artwork's own ratio sets the height, so the overlay lands where it was drawn for. */}
      <img className="block w-full" src={image} alt="" />

      <div className="absolute inset-0">{children}</div>
    </div>
  );
}

interface IProps {
  insurance: ILifeInsurance;
  template: ICardTemplate;
  /** The card is printed with the record being viewed, not with whatever is on the insurance. */
  patientRecord: IPatientRecord;
}

export default function InsuranceCard({ insurance, template, patientRecord }: IProps) {
  //! state
  const { t } = useTranslation();

  //! function
  const formatDate = (value?: string | dayjs.Dayjs) => (value ? dayjs(value).format("DD/MM/YYYY") : "");

  //! render
  return (
    // Both faces at once, side by side; stacked where two would not fit.
    <div className="w-full max-w-[400px] sm:max-w-[812px] grid grid-cols-1 sm:grid-cols-2 gap-3">
      <CardFace image={template.front}>
        {/* 12dp sides, 24dp bottom, 10sp text, 2dp line gap, 8dp between columns */}
        <div className="absolute inset-x-[3.48cqw] bottom-[6.96cqw] flex items-end gap-[2.32cqw] text-[length:2.9cqw] leading-tight">
          <div className="flex flex-col gap-[0.58cqw] flex-1 min-w-0">
            <span className="truncate font-bold uppercase">{patientRecord.patient_name}</span>
            <span className="truncate">{formatDate(patientRecord.patient_dob)}</span>
          </div>

          <div className="flex flex-col gap-[0.58cqw] items-end shrink-0">
            {insurance.card_no ? <span className="font-bold">{insurance.card_no}</span> : null}
            {insurance.end_date ? <span>{t("user.profile.insurance.expiry", { date: formatDate(insurance.end_date) })}</span> : null}
          </div>
        </div>
      </CardFace>

      <CardFace image={template.back}>
        {/* 90dp square, 36dp from the top, centred */}
        {insurance.card_no ? (
          <div className="absolute top-[10.43cqw] left-1/2 -translate-x-1/2 w-[26.1cqw] aspect-square">
            <QRCode
              className="[&_canvas]:!w-full [&_canvas]:!h-full"
              style={{ width: "100%", height: "100%", padding: "6%" }}
              value={insurance.card_no}
              size={300}
              bgColor="#ffffff"
              bordered={false}
            />
          </div>
        ) : null}
      </CardFace>
    </div>
  );
}
